import { useState, useEffect } from 'react';
import axios from 'axios';
import { GraduationCap, Building2, Users } from 'lucide-react';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Subscribers() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/subscribers`);
        if (mounted) setSubscribers(res.data || []);
      } catch {
        if (mounted) setSubscribers([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const academic = subscribers.filter(s => s.category === 'Academic');
  const corporate = subscribers.filter(s => s.category === 'Corporate');

  const renderCard = (s) => (
    <div
      key={s.id}
      className="bg-white border border-slate-200 rounded-xl p-6 flex items-center gap-4 hover:border-slate-900 hover:shadow-md transition-all"
      data-testid={`subscriber-card-${s.id}`}
    >
      {s.logo_url ? (
        <img
          src={s.logo_url}
          alt={s.name}
          className="w-14 h-14 object-contain rounded-lg bg-slate-50 border border-slate-100"
          loading="lazy"
        />
      ) : (
        <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center">
          {s.category === 'Corporate' ? (
            <Building2 className="w-7 h-7 text-slate-500" />
          ) : (
            <GraduationCap className="w-7 h-7 text-slate-500" />
          )}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-serif font-semibold text-slate-900 truncate" data-testid={`subscriber-name-${s.id}`}>{s.name}</p>
        <p className="text-xs text-slate-500 mt-1">{s.category}</p>
      </div>
    </div>
  );

  const renderSection = (title, icon, items, testId) => (
    <div className="mb-12" data-testid={testId}>
      <div className="flex items-center gap-3 mb-6">
        {icon}
        <h2 className="text-2xl font-serif font-bold text-slate-900">{title}</h2>
        <span className="text-sm text-slate-500">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <p className="text-slate-500 text-sm">No subscribers yet in this category.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(renderCard)}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-28 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-slate-200 rounded-full text-xs font-mono text-slate-600 mb-4">
              <Users className="w-3.5 h-3.5" />
              Trusted by leading institutions and enterprises
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-slate-900 mb-4" data-testid="subscribers-page-title">
              Our Subscribers
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              A growing community of business schools, universities, and corporate L&D teams that rely on The Case Verse for premium teaching content.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-16" data-testid="subscribers-loading">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900"></div>
            </div>
          ) : subscribers.length === 0 ? (
            <div className="text-center py-16 bg-white border border-dashed border-slate-300 rounded-xl" data-testid="subscribers-empty">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-700 font-medium">Our subscriber list is coming soon.</p>
              <p className="text-slate-500 text-sm mt-2">Check back shortly — we are onboarding new institutions every week.</p>
            </div>
          ) : (
            <>
              {renderSection('Academic Institutions', <GraduationCap className="w-6 h-6 text-orange-600" />, academic, 'section-academic')}
              {renderSection('Corporate L&D', <Building2 className="w-6 h-6 text-orange-600" />, corporate, 'section-corporate')}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
